import { CancelExamEventModal } from '@exam-center/exam-event-management';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useCallback, useMemo, useState } from 'react';

import {
  TemporarilyCloseExamEventModal,
  UpdateToOpenForRegistrationConfirmationModal,
} from '../../containers';

import type {
  CancelExamEventArgs,
  ExamEventDetailsEditModalAction,
  ExamEventDetailsEditModalContextValue,
  ModalData,
  OpenForRegistrationArgs,
  SetActionArgs,
  TemporarilyCloseExamEventArgs,
} from './types';

const ExamEventDetailsEditModalContext = createContext<
  ExamEventDetailsEditModalContextValue | undefined
>(undefined);

export default function ExamEventDetailsEditModalProvider(props: Readonly<PropsWithChildren>) {
  const { children } = props;
  const [modalData, setModalData] = useState<ModalData | null>(null);
  const [action, setAction] = useState<ExamEventDetailsEditModalAction | null>(null);

  const handleSetAction = useCallback(
    (args: SetActionArgs) => {
      const { action, data } = args;
      setAction(action);
      setModalData(data ?? null);
    },
    [setAction],
  );

  const handleClose = useCallback(() => {
    setAction(null);
  }, []);

  const contextValue: ExamEventDetailsEditModalContextValue = useMemo(() => {
    return {
      setAction: handleSetAction,
      close: handleClose,
    };
  }, [handleSetAction, handleClose]);

  return (
    <ExamEventDetailsEditModalContext.Provider value={contextValue}>
      {children}
      <CancelExamEventModal
        open={action === 'cancel'}
        onClose={handleClose}
        {...(modalData as CancelExamEventArgs)}
      />
      <TemporarilyCloseExamEventModal
        open={action === 'temporarily-close'}
        onClose={handleClose}
        {...(modalData as TemporarilyCloseExamEventArgs)}
      />
      <UpdateToOpenForRegistrationConfirmationModal
        open={action === 'open-for-registration'}
        onClose={handleClose}
        onAccept={modalData?.onSubmit as OpenForRegistrationArgs['onSubmit']}
      />
    </ExamEventDetailsEditModalContext.Provider>
  );
}

export { ExamEventDetailsEditModalContext };
